import { useState, useEffect } from 'react';
import { Search, Package, AlertCircle, Loader2, ImageIcon, BadgePercent } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { SalesSummary } from '../components/SalesSummary';

interface Discount {
  id: number;
  nombre: string;
  valor_descuento: number;
  fecha_inicio: string;
  fecha_fin: string;
  cant_min: number | null;
  cant_max: number | null;
  calculo_descuento: 'porcentaje' | 'monto fijo';
  tipo_descuento: string;
  active: boolean;
}

// Use extends Discount to ensure TypeScript knows we're using the Discount interface
interface DiscountDisplay extends Pick<Discount, 'id' | 'nombre' | 'valor_descuento' | 'fecha_fin' | 'cant_min' | 'cant_max' | 'calculo_descuento' | 'tipo_descuento'> {
  label: string;
}

interface ProductVariant {
  id: number;
  nombre: string;
  sku: string;
  precio_local: number;
  stock_disponible: number;
  product_name: string;
  image_url: string | null;
  producto_id: number; // Added to link to product for discount lookup
  categoria_id?: number; // Category ID for category-based discounts
  discounts?: DiscountDisplay[]; // Available discounts for this variant
}

// Event name for when user changes their configured local
const LOCAL_CHANGED_EVENT = 'local-config-changed';

// Format price with locale
const formatPrice = (price: number) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

// Helper to format date to display in UI
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Use the Discount type to properly type discounts
// This ensures TypeScript knows we're using the Discount type

// Function to fetch applicable discounts for variants
const fetchDiscountsForVariants = async (companyId: string, localId: number, variants: ProductVariant[]) => {
  try {
    // Get current date in ISO format for comparison
    const currentDate = new Date().toISOString().split('T')[0];
    console.log('Fetching discounts for date:', currentDate);
    
    // Fetch active discounts
    const { data: discountsData, error: discountsError } = await supabase
      .from('descuentos')
      .select('id, nombre_descuento, valor_descuento, fecha_inicio, fecha_fin, cant_min, cant_max, calculo_descuento, tipo_descuento, activo')
      .eq('company_id', companyId)
      .eq('activo', true)
      .lte('fecha_inicio', currentDate)
      .gte('fecha_fin', currentDate);
    
    if (discountsError) {
      console.error('Error fetching active discounts:', discountsError);
      return;
    }
    
    if (!discountsData || discountsData.length === 0) {
      console.log('No active discounts found');
      return;
    }
    
    console.log(`Found ${discountsData.length} active discounts`);
    
    // Get unique product IDs from variants
    const productIds = [...new Set(variants.map(v => v.producto_id))];
    
    // Fetch product-discount relationships
    const { data: discountProducts, error: discountProductsError } = await supabase
      .from('descuentos_productos')
      .select('descuento_id, producto_id')
      .eq('company_id', companyId)
      .in('descuento_id', discountsData.map(d => d.id))
      .in('producto_id', productIds);
    
    if (discountProductsError) {
      console.error('Error fetching discount-product relationships:', discountProductsError);
      return;
    }
    
    // Fetch category-discount relationships
    const { data: discountCategories, error: discountCategoriesError } = await supabase
      .from('descuentos_categorias')
      .select('descuento_id, categoria_id')
      .eq('company_id', companyId)
      .in('descuento_id', discountsData.map(d => d.id));
    
    if (discountCategoriesError) {
      console.error('Error fetching discount categories:', discountCategoriesError);
      return;
    }
    
    // Fetch discount applications (for store-specific discounts)
    const { data: discountApplications, error: discountAppsError } = await supabase
      .from('descuentos_aplicaciones')
      .select('descuento_id, local_id, tipo')
      .eq('company_id', companyId)
      .in('descuento_id', discountsData.map(d => d.id));
    
    if (discountAppsError) {
      console.error('Error fetching discount applications:', discountAppsError);
      return;
    }
    
    // Get product categories for lookup
    const { data: productsData, error: productsError } = await supabase
      .from('productos')
      .select('id, categoria_id')
      .eq('company_id', companyId)
      .in('id', productIds);
    
    if (productsError) {
      console.error('Error fetching product categories:', productsError);
      return;
    }
    
    // Create product ID to category ID mapping with proper type declaration
    const productCategoryMap: {[key: number]: number} = {};
    productsData.forEach(product => {
      productCategoryMap[product.id] = product.categoria_id;
    });
    
    // Process variants and assign applicable discounts
    const updatedVariants = variants.map(variant => {
      const productId = variant.producto_id;
      const categoryId = productCategoryMap[productId];
      
      // Find product-specific discounts
      const productDiscounts = discountProducts
        ? discountsData.filter(d => 
            discountProducts.some(dp => 
              dp.descuento_id === d.id && dp.producto_id === productId
            )
          )
        : [];
      
      // Find category-specific discounts
      const categoryDiscounts = discountCategories && categoryId
        ? discountsData.filter(d => 
            discountCategories.some(dc => 
              dc.descuento_id === d.id && dc.categoria_id === categoryId
            )
          )
        : [];
      
      // Combine and filter by store/local applicability
      const applicableDiscounts = [...productDiscounts, ...categoryDiscounts].filter(d => {
        // Get store-specific applications for this discount
        const storeApplications = discountApplications
          ? discountApplications.filter(da => 
              da.descuento_id === d.id && da.tipo === 'local'
            )
          : [];
        
        // If no store applications exist, discount applies everywhere
        if (storeApplications.length === 0) return true;
        
        // Otherwise check if this specific local/store is included
        return storeApplications.some(sa => sa.local_id === localId);
      });
      
      // Format discounts for display with enhanced label formatting
      const formattedDiscounts = applicableDiscounts.map(d => {
        // Create appropriate discount label based on type and conditions
        let discountLabel = '';
        
        // Use consistent discount label format for all discount types
        if (d.cant_min !== null && d.cant_max !== null && d.cant_min === d.cant_max) {
          // Fixed quantity discount (e.g., 2x1) - changed order to MxN
          discountLabel = `${d.cant_min}x${d.cant_min - d.valor_descuento}`;
        } else if (d.cant_min !== null) {
          // Quantity-based discount with minimum quantity requirement - removed 'U' suffix
          if (d.calculo_descuento === 'porcentaje') {
            discountLabel = `${d.valor_descuento}% OFF a partir de ${d.cant_min}u`;
          } else if (d.calculo_descuento === 'monto_fijo') {
            discountLabel = `${d.valor_descuento}$ OFF a partir de ${d.cant_min}u`;
          }
        } else {
          // All other cases use the simple discount format
          if (d.calculo_descuento === 'porcentaje') {
            discountLabel = `${d.valor_descuento}% OFF`;
          } else if (d.calculo_descuento === 'monto_fijo') {
            discountLabel = `${d.valor_descuento}$ OFF`;
          } else {
            // Fallback for any other cases
            discountLabel = `${d.valor_descuento}`;
          }
        }
        
        return {
          id: d.id,
          nombre: d.nombre_descuento, // Fix property name to match updated database column
          valor_descuento: d.valor_descuento,
          fecha_fin: formatDate(d.fecha_fin),
          cant_min: d.cant_min,
          cant_max: d.cant_max,
          calculo_descuento: d.calculo_descuento,
          tipo_descuento: d.tipo_descuento,
          label: discountLabel
        };
      });
      
      // Create a new variant object rather than mutating the original
      return {
        ...variant,
        discounts: formattedDiscounts
      };
    });
    
    // Update the original array with all the variants at once
    for (let i = 0; i < variants.length; i++) {
      variants[i] = updatedVariants[i];
    }
    
    console.log('Successfully processed and assigned discounts to variants');
  } catch (err) {
    console.error('Error in fetchDiscountsForVariants:', err);
  }
};

export const SalesRegister = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  // We're using local variables inside async functions instead of state for immediate access
  const [signedUrls, setSignedUrls] = useState<Record<number, { thumbnail: string }>>({});
  const [loadingThumbnails, setLoadingThumbnails] = useState<Record<number, boolean>>({});
  const [selectedItems, setSelectedItems] = useState<{
    variantId: number;
    productName: string;
    variantName: string;
    quantity: number;
    price: number;
  }[]>([]);

  useEffect(() => {
    fetchInitialData();
    
    // Add event listener for local configuration changes
    window.addEventListener(LOCAL_CHANGED_EVENT, fetchInitialData);
    
    // Add event listener for stock updates after sale
    const handleStockUpdate = (event: CustomEvent<{ items: { variantId: number, quantity: number }[] }>) => {
      const soldItems = event.detail.items;
      
      // Update local variant stock without refetching from the database
      setVariants(prevVariants => {
        return prevVariants.map(variant => {
          // Find if this variant was sold in the recent sale
          const soldItem = soldItems.find(item => item.variantId === variant.id);
          if (soldItem) {
            // Reduce the stock by sold quantity
            return {
              ...variant,
              stock_disponible: variant.stock_disponible - soldItem.quantity
            };
          }
          return variant;
        });
      });
    };
    
    // TypeScript requires casting the Event to CustomEvent
    window.addEventListener('sale-stock-update', handleStockUpdate as EventListener);
    
    // Clean up event listeners on component unmount
    return () => {
      window.removeEventListener(LOCAL_CHANGED_EVENT, fetchInitialData);
      window.removeEventListener('sale-stock-update', handleStockUpdate as EventListener);
    };
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      const companyId = profile?.id_company as string | null;
      if (!companyId) throw new Error('No company associated with user');

      // Determine local: pick first PoS local for this company
      const { data: locales, error: localesError } = await supabase
        .from('locales')
        .select('id')
        .eq('company_id', companyId)
        .eq('tipo', 'PoS')
        .limit(1)
        .single();

      if (localesError) throw localesError;
      const localId: number = locales.id;

      // Fetch variants with stock for the configured local using the local variable
      const { data: variantsData, error: variantsError } = await supabase
        .from('stock')
        .select(`
          cantidad_disponible,
          variantes!inner (
            id,
            nombre,
            sku,
            precio_local,
            productos!inner (
              id,
              nombre,
              categoria_id
            )
          )
        `)
        .eq('company_id', companyId)
        .eq('local_id', localId)
        .gt('cantidad_disponible', 0);

      if (variantsError) throw variantsError;

      // Fetch images for variants
      const variantIds = variantsData?.map(v => (v.variantes as any).id) || [];
      const { data: imagesData, error: imagesError } = await supabase
        .from('productos_imagenes')
        .select('variante_id, url')
        .eq('company_id', companyId)
        .in('variante_id', variantIds)
        .eq('principal', true);

      if (imagesError) throw imagesError;

      // Create image URL map and loading state
      const signedUrlsMap: Record<number, { thumbnail: string }> = {};
      const loadingThumbnailsMap: Record<number, boolean> = {};
      
      // First pass: set all images to use original URLs as fallback
      for (const img of imagesData || []) {
        if (img.url) {
          signedUrlsMap[img.variante_id] = { thumbnail: img.url };
          loadingThumbnailsMap[img.variante_id] = true;
        }
      }
      
      // Update state immediately to show images with original URLs
      setSignedUrls(signedUrlsMap);
      setLoadingThumbnails(loadingThumbnailsMap);
      
      // Second pass: asynchronously fetch optimized images from the 'opt' folder
      for (const img of imagesData || []) {
        // Check if it's a filename-only format (new system) or a full URL (legacy)
        if (img.url && !img.url.startsWith('http')) {
          // New format - just filename, need to construct paths and check for optimized version
          try {
            const fileName = img.url;
            const optFilePath = `${companyId}/${img.variante_id}/opt/${fileName}`;
            console.log('Fetching optimized image from:', optFilePath);
            
            // Try to get the optimized version first
            const { data: optData, error: optError } = await supabase
              .storage
              .from('productos')
              .createSignedUrl(optFilePath, 3600);
            
            if (!optError && optData?.signedUrl) {
              console.log('Found optimized image in opt folder');
              // Update URLs individually as they become available
              setSignedUrls(prev => ({
                ...prev,
                [img.variante_id]: {
                  thumbnail: optData.signedUrl
                }
              }));
            } else {
              // Fallback to full version if optimized not found
              console.log('Optimized image not found, trying full version');
              const fullFilePath = `${companyId}/${img.variante_id}/full/${fileName}`;
              
              const { data: fullData } = await supabase
                .storage
                .from('productos')
                .createSignedUrl(fullFilePath, 3600);
              
              if (fullData?.signedUrl) {
                setSignedUrls(prev => ({
                  ...prev,
                  [img.variante_id]: {
                    thumbnail: fullData.signedUrl
                  }
                }));
              }
            }
            
            // Mark this thumbnail as loaded
            setLoadingThumbnails(prev => ({
              ...prev,
              [img.variante_id]: false
            }));
          } catch (err) {
            console.error('Error fetching optimized image:', err);
            setLoadingThumbnails(prev => ({
              ...prev,
              [img.variante_id]: false
            }));
          }
        } else if (img.url && img.url.includes('supabase.co/storage/v1/object')) {
          // Legacy URL format - extract paths and try to create optimized URL
          try {
            // Extract bucket and file path from the URL
            const regex = /supabase\.co\/storage\/v1\/object\/public\/([^\/]+)\/(.+)/;
            const match = img.url.match(regex);
            
            if (match && match.length >= 3) {
              const bucket = match[1];
              const filePath = match[2];
              
              // Check if this is a product image that might have an optimized version
              if (filePath.startsWith('productos/')) {
                // Try to extract variant ID and filename
                const pathParts = filePath.split('/');
                if (pathParts.length >= 3) {
                  const variantId = pathParts[1];
                  const fileName = pathParts[pathParts.length - 1];
                  
                  // Try to get the optimized version
                  const optFilePath = `productos/${variantId}/opt/${fileName}`;
                  console.log('Trying legacy optimized path:', optFilePath);
                  
                  const { data: optData, error: optError } = await supabase
                    .storage
                    .from(bucket)
                    .createSignedUrl(optFilePath, 3600);
                  
                  if (!optError && optData?.signedUrl) {
                    console.log('Found optimized version of legacy image');
                    setSignedUrls(prev => ({
                      ...prev,
                      [img.variante_id]: {
                        thumbnail: optData.signedUrl
                      }
                    }));
                    
                    setLoadingThumbnails(prev => ({
                      ...prev,
                      [img.variante_id]: false
                    }));
                    continue;
                  }
                }
              }
              
              // Fallback to transformation if no optimized version found
              const { data: thumbnailData } = await supabase
                .storage
                .from(bucket)
                .createSignedUrl(filePath, 3600, {
                  transform: {
                    width: 300,
                    height: 300,
                    resize: 'cover'
                  }
                });
              
              if (thumbnailData?.signedUrl) {
                // Update URLs individually as they become available
                setSignedUrls(prev => ({
                  ...prev,
                  [img.variante_id]: {
                    thumbnail: thumbnailData.signedUrl
                  }
                }));
                
                // Mark this thumbnail as loaded
                setLoadingThumbnails(prev => ({
                  ...prev,
                  [img.variante_id]: false
                }));
              }
            } else {
              console.error('Failed to extract path from URL:', img.url);
              setLoadingThumbnails(prev => ({
                ...prev,
                [img.variante_id]: false
              }));
            }
          } catch (err) {
            console.error('Error creating signed URL:', err);
            // Mark this thumbnail as loaded even on error
            setLoadingThumbnails(prev => ({
              ...prev,
              [img.variante_id]: false
            }));
          }
        } else if (img.url) {
          // For non-Supabase URLs, mark as loaded immediately
          setLoadingThumbnails(prev => ({
            ...prev,
            [img.variante_id]: false
          }));
        }
      }

      // Process variants data
      const processedVariants = variantsData?.map(item => {
        // Use proper type assertions to fix TypeScript errors
        const variante = item.variantes as any;
        const producto = variante.productos as any;
        
        return {
          id: variante.id,
          nombre: variante.nombre,
          sku: variante.sku,
          precio_local: variante.precio_local,
          stock_disponible: item.cantidad_disponible,
          product_name: producto.nombre,
          image_url: imagesData?.find(img => img.variante_id === variante.id)?.url || null,
          producto_id: producto.id, // Add product ID for discount lookup
          categoria_id: producto.categoria_id, // Add category ID for category-based discounts
          discounts: [] as DiscountDisplay[] // Initialize empty discounts array with proper type
        };
      }) || [];

      // Fetch available discounts for the current date and store
      await fetchDiscountsForVariants(companyId, localId, processedVariants);
      
      setVariants(processedVariants);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const filteredVariants = variants.filter(variant => {
    const searchLower = searchTerm.toLowerCase().trim();
    return (
      variant.product_name.toLowerCase().includes(searchLower) ||
      variant.nombre.toLowerCase().includes(searchLower) ||
      variant.sku.toLowerCase().includes(searchLower)
    );
  });

  // Use the global formatPrice function defined above

  // We're using the extended SaleItem interface from SalesSummary.tsx

  // Calculate discount value based on discount type, variant price, and quantity
  const calculateDiscountValue = (
    discount: DiscountDisplay,
    variantPrice: number,
    quantity: number
  ) => {
    // Simple discount logic
    if (discount.tipo_descuento === 'Simple') {
      if (discount.calculo_descuento === 'porcentaje') {
        // Percentage discount: (variant price * valor_descuento / 100) * quantity
        return (variantPrice * discount.valor_descuento / 100) * quantity;
      } else { // monto_fijo
        // Fixed discount: valor_descuento * quantity
        return discount.valor_descuento;
      }
    }
    if (discount.tipo_descuento === 'Monto directo') {
      return discount.valor_descuento;
    }
    
    // Quantity-Based Discount: equal min and max
    if (discount.cant_min !== null && discount.cant_max !== null && discount.cant_min === discount.cant_max) {
      // Only apply if quantity matches exactly cant_min
      if (quantity === discount.cant_min) {
        // Discount value = (cant_min - valor_descuento) * variant price
        return (discount.valor_descuento) * variantPrice;
      }
      return 0; // No discount if quantity doesn't match
    }
    
    // Quantity-Based Discount with minimum quantity
    if (discount.cant_min !== null) {
      // Only apply if quantity >= cant_min
      if (quantity >= discount.cant_min) {
        if (discount.calculo_descuento === 'porcentaje') {
          // Percentage discount: (variant price * valor_descuento / 100) * quantity
          return (variantPrice * discount.valor_descuento / 100) * quantity;
        } else { // monto_fijo
          // Fixed discount: valor_descuento * quantity
          return discount.valor_descuento;
        }
      }
      return 0; // No discount if quantity < cant_min
    }
    
    // Default case - no applicable discount
    return 0;
  };

  // Check if a discount should be applied based on quantity conditions
  const shouldApplyDiscount = (discount: DiscountDisplay, quantity: number) => {
    // For equal min/max quantity discounts, quantity must match exactly
    if (discount.cant_min !== null && discount.cant_max !== null && discount.cant_min === discount.cant_max) {
      return quantity === discount.cant_min;
    }
    
    // For min quantity discounts, quantity must be >= min
    if (discount.cant_min !== null) {
      return quantity >= discount.cant_min;
    }
    
    // Simple discounts always apply
    return true;
  };

  const handleVariantClick = (variant: ProductVariant) => {
    setSelectedItems(prev => {
      const existingItem = prev.find(item => item.variantId === variant.id);
      if (existingItem) {
        // Update quantity for existing item
        const updatedQuantity = existingItem.quantity + 1;
        
        // Update discounts based on new quantity
        const updatedDiscounts = variant.discounts?.filter(discount => 
          shouldApplyDiscount(discount, updatedQuantity)
        ).map(discount => ({
          discountId: discount.id,
          discountName: discount.nombre,
          discountValue: calculateDiscountValue(discount, variant.precio_local, updatedQuantity),
          discountType: discount.tipo_descuento,
          minQuantity: discount.cant_min,
          maxQuantity: discount.cant_max,
          calculoDescuento: discount.calculo_descuento,
          valorDescuento: discount.valor_descuento
        })).filter(d => d.discountValue > 0) || [];
        
        return prev.map(item =>
          item.variantId === variant.id
            ? { ...item, quantity: updatedQuantity, discounts: updatedDiscounts }
            : item
        );
      }
      
      // Add new item with applicable discounts
      const initialDiscounts = variant.discounts?.filter(discount => 
        shouldApplyDiscount(discount, 1) // Initial quantity is 1
      ).map(discount => ({
        discountId: discount.id,
        discountName: discount.nombre,
        discountValue: calculateDiscountValue(discount, variant.precio_local, 1),
        discountType: discount.tipo_descuento,
        minQuantity: discount.cant_min,
        maxQuantity: discount.cant_max,
        calculoDescuento: discount.calculo_descuento,
        valorDescuento: discount.valor_descuento
      })).filter(d => d.discountValue > 0) || [];
      
      return [...prev, {
        variantId: variant.id,
        productName: variant.product_name,
        variantName: variant.nombre,
        quantity: 1,
        price: variant.precio_local,
        discounts: initialDiscounts
      }];
    });
  };

  const handleUpdateQuantity = (variantId: number, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveItem(variantId);
      return;
    }
    
    const variant = variants.find(v => v.id === variantId);
    if (!variant || quantity > variant.stock_disponible) return;

    setSelectedItems(prev =>
      prev.map(item =>
        item.variantId === variantId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const handleRemoveItem = (variantId: number) => {
    setSelectedItems(prev => prev.filter(item => item.variantId !== variantId));
  };

  const handleResetSale = () => {
    setSelectedItems([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Package className="w-8 h-8 text-gray-700" />
          <h1 className="text-2xl font-semibold text-gray-900">Registro de ventas</h1>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Left Column - Product Catalog */}
        <div className="flex-1">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {/* Search Filter */}
          <div className="mb-6">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nombre o SKU del producto..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredVariants.map((variant) => (
              <div
                key={variant.id}
                className={`bg-white rounded-lg shadow-sm p-4 border transition-all ${
                  variant.stock_disponible > 0
                    ? 'border-gray-200 hover:border-blue-500 hover:border-2 hover:shadow-md cursor-pointer'
                    : 'border-gray-200 opacity-50 cursor-not-allowed'
                }`}
                onClick={() => handleVariantClick(variant)}
                role="button"
                tabIndex={0}
              >
                {/* Image */}
                <div className="aspect-[4/3] mb-3 bg-gray-100 rounded-lg overflow-hidden relative">
                  {(signedUrls[variant.id]?.thumbnail || variant.image_url) ? (
                    <>
                      {/* Show loading spinner while image is loading */}
                      {loadingThumbnails[variant.id] && (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                          <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
                        </div>
                      )}
                      
                      {/* Always show the image, but with opacity based on loading state */}
                      <img
                        src={signedUrls[variant.id]?.thumbnail || variant.image_url || ''}
                        alt={`${variant.product_name} - ${variant.nombre}`}
                        className={`w-full h-full object-cover transition-opacity duration-300 ${loadingThumbnails[variant.id] ? 'opacity-40' : 'opacity-100'}`}
                        loading="lazy"
                        width="300"
                        height="225"
                        onLoad={() => {
                          // When the image loads, remove the loading state
                          setLoadingThumbnails(prev => ({
                            ...prev,
                            [variant.id]: false
                          }));
                        }}
                        onError={() => {
                          // If image fails to load, remove loading state
                          console.error(`Failed to load image for variant ${variant.id}`);
                          setLoadingThumbnails(prev => ({
                            ...prev,
                            [variant.id]: false
                          }));
                        }}
                      />
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <h3 className="font-bold text-sm mb-1 line-clamp-2">
                  {variant.product_name} - {variant.nombre}
                </h3>
                
                {/* SKU and Stock on the same line */}
                <div className="flex justify-between items-center mb-2">
                  <p className="text-xs text-gray-500">SKU: {variant.sku}</p>
                  <p className={`text-xs font-medium ${
                    variant.stock_disponible > 0 ? 'text-gray-600' : 'text-red-600'
                  }`}>
                    Stock: {variant.stock_disponible}
                  </p>
                </div>
                
                {/* Price and discount badges on the same row */}
                <div className="mb-2">
                  <div className="flex items-center flex-wrap">
                    <p className="text-[#2E7D32] font-bold text-sm mr-2">
                      {formatPrice(variant.precio_local)}
                    </p>
                    
                    {/* Discount badges inline with price */}
                    {variant.discounts && variant.discounts.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {variant.discounts.map((discount) => (
                          <div 
                            key={discount.id}
                            className="group relative inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700 cursor-help"
                          >
                            {/* Making the entire badge hoverable with tooltip */}
                            <div className="invisible group-hover:visible absolute bottom-full left-1/2 transform -translate-x-1/2 w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                              <p><b>{discount.nombre}</b></p>
                              <p>Válido hasta: {discount.fecha_fin}</p>
                              {discount.cant_min && <p>Cantidad mínima: {discount.cant_min}</p>}
                              {discount.cant_max && <p>Cantidad máxima: {discount.cant_max}</p>}
                            </div>
                            
                            {/* Display BadgePercent icon for all discount types */}
                            <BadgePercent className="w-4 h-4 text-red-600 flex-shrink-0" />
                            
                            <span className="font-medium">{discount.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column - Sales Summary */}
        <div className="w-96">
          <SalesSummary
            items={selectedItems}
            onUpdateQuantity={handleUpdateQuantity}
            onRemoveItem={handleRemoveItem}
            onReset={handleResetSale}
          />
        </div>
      </div>
    </div>
  );
};